/**
 * Phase 16.2 + 16.3 — Admin Audit page (/admin/audit).
 *
 * Browser-side equivalent of the CLI runbook
 * /app/backend/tools/reconcile_live_gifts.py. Admin-only, financial control
 * surface:
 *
 *   Read-only (16.2):
 *     • Run full / payout-ready platform audits
 *     • Download three CSVs (full, summary, payout-ready)
 *     • Drift investigation w/ TEST_ACCOUNT / LIKELY_REAL / UNKNOWN classification
 *
 *   Phase 16.3 additions (this file):
 *     • Live Gift Economy KPI dashboard (/admin/audit/economy-kpis)
 *     • Founder visibility / Platform Health summary
 *     • Drift Management panel: dry-run → CONFIRM → reset
 *       (only TEST_ACCOUNT-classified wallets — backend enforces this)
 *     • Tri-state Payout Readiness banner (green / yellow / red)
 *
 * Cannot approve/deny/mark-paid (those stay in /admin/payouts).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useRequireAdmin } from "@/lib/useRequireAdmin";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";

const fmtMoney = (v) =>
  typeof v === "number"
    ? v.toLocaleString("en-US", { style: "currency", currency: "USD" })
    : "—";

const fmtInt = (v) =>
  typeof v === "number" && Number.isFinite(v) ? v.toLocaleString("en-US") : (v ?? "—");

const Card = ({ label, value, tone = "default", testId, hint }) => {
  const toneCls = {
    default: "border-white/10 bg-white/5 text-white",
    good: "border-emerald-500/30 bg-emerald-500/10 text-emerald-100",
    warn: "border-amber-500/30 bg-amber-500/10 text-amber-100",
    bad: "border-rose-500/30 bg-rose-500/10 text-rose-100",
    accent: "border-fuchsia-500/30 bg-fuchsia-500/10 text-fuchsia-100",
  }[tone];
  return (
    <div
      className={`rounded-xl border ${toneCls} px-4 py-3 min-w-[160px]`}
      data-testid={testId}
    >
      <div className="text-[10px] uppercase tracking-widest opacity-70 font-bold">{label}</div>
      <div className="text-2xl font-black mt-0.5">{value}</div>
      {hint && <div className="text-[10px] opacity-70 mt-1">{hint}</div>}
    </div>
  );
};

const Banner = ({ tone = "warn", icon, title, children, testId }) => {
  const cls = {
    warn: "border-amber-500/40 bg-amber-500/10 text-amber-100",
    bad: "border-rose-500/40 bg-rose-500/10 text-rose-100",
    good: "border-emerald-500/40 bg-emerald-500/10 text-emerald-100",
  }[tone];
  return (
    <div className={`rounded-xl border ${cls} px-4 py-3 mb-4`} data-testid={testId}>
      <div className="flex items-start gap-3">
        <div className="text-2xl flex-shrink-0">{icon}</div>
        <div className="flex-1 text-sm">
          <div className="font-bold">{title}</div>
          <div className="opacity-90 mt-0.5">{children}</div>
        </div>
      </div>
    </div>
  );
};

const SectionHeader = ({ kicker, title, subtitle }) => (
  <div className="mb-3">
    <span className="text-[10px] uppercase tracking-[0.3em] text-fuchsia-300 font-bold">{kicker}</span>
    <h2 className="text-xl sm:text-2xl font-black mt-0.5">{title}</h2>
    {subtitle && <p className="text-xs text-gray-400 mt-1 max-w-2xl">{subtitle}</p>}
  </div>
);

export default function AdminAuditPage() {
  const { authChecked } = useRequireAdmin();
  const [running, setRunning] = useState(false);
  const [minUsd, setMinUsd] = useState(25);
  const [report, setReport] = useState(null);
  const [driftDetail, setDriftDetail] = useState(null);
  const [loadingDrift, setLoadingDrift] = useState(false);

  // Phase 16.3 — KPI dashboard
  const [kpis, setKpis] = useState(null);
  const [loadingKpis, setLoadingKpis] = useState(false);

  // Phase 16.3 — Drift reset flow
  const [dryRunResult, setDryRunResult] = useState(null);
  const [resetResult, setResetResult] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [dryRunning, setDryRunning] = useState(false);
  const [resetting, setResetting] = useState(false);

  const didAutoRun = useRef(false);

  const runAudit = useCallback(async (filterMin) => {
    setRunning(true);
    try {
      const r = await api.post(`/admin/audit/run?min_usd=${filterMin}`);
      setReport(r.data);
      toast.success("Audit complete — read-only");
      if (r.data?.totals?.warnings?.do_not_pay_present) {
        loadDriftDetail();
      } else {
        setDriftDetail(null);
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Audit failed");
    } finally {
      setRunning(false);
    }
  }, []);

  const loadDriftDetail = async () => {
    setLoadingDrift(true);
    try {
      const r = await api.get(`/admin/audit/drift-detail`);
      setDriftDetail(r.data);
    } catch (e) {
      toast.error("Couldn't load drift detail");
    } finally {
      setLoadingDrift(false);
    }
  };

  const loadKpis = useCallback(async () => {
    setLoadingKpis(true);
    try {
      const r = await api.get(`/admin/audit/economy-kpis`);
      setKpis(r.data);
    } catch (e) {
      toast.error("Couldn't load economy KPIs");
    } finally {
      setLoadingKpis(false);
    }
  }, []);

  const runDryRun = async () => {
    setDryRunning(true);
    setResetResult(null);
    try {
      const r = await api.post(`/admin/audit/reset-drift?dry_run=true`);
      setDryRunResult(r.data);
      toast.success(`Dry-run complete — ${r.data?.summary?.would_mutate ?? 0} test wallet(s) would be reset`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Dry-run failed");
    } finally {
      setDryRunning(false);
    }
  };

  const runReset = async () => {
    if (confirmText.trim() !== "CONFIRM") {
      toast.error('Type "CONFIRM" exactly to proceed');
      return;
    }
    setResetting(true);
    try {
      const r = await api.post(`/admin/audit/reset-drift?dry_run=false`);
      setResetResult(r.data);
      toast.success(`Reset complete — ${r.data?.summary?.mutated ?? 0} test wallet(s) cleared`);
      setConfirmText("");
      setDryRunResult(null);
      // Refresh audit + KPIs to reflect the new state
      await Promise.all([runAudit(minUsd), loadKpis()]);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Reset failed");
    } finally {
      setResetting(false);
    }
  };

  // Auto-run on mount: full audit + KPIs in parallel.
  useEffect(() => {
    if (!authChecked || didAutoRun.current) return;
    didAutoRun.current = true;
    runAudit(25);
    loadKpis();
  }, [authChecked, runAudit, loadKpis]);

  if (!authChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-400">
        Checking admin access…
      </div>
    );
  }

  const totals = report?.totals;
  const counts = totals?.counts || {};
  const usd = totals?.usd || {};
  const warnings = totals?.warnings || {};
  const integrity = totals?.integrity || {};
  const rows = report?.preview_rows || [];

  // ── Payout readiness verdict (tri-state) ─────────────────────
  // Red:    orphan revenue present (cannot trust ledger)
  // Yellow: drift present but eligible creators unaffected
  // Green:  no orphans, no drift
  const verdict = (() => {
    if (!totals) return null;
    if (warnings.orphans_present) return "red";
    if (warnings.drift_present || warnings.do_not_pay_present) return "yellow";
    return "green";
  })();

  // Build a token-authenticated download URL for the CSV streams.
  const token = (() => {
    try { return localStorage.getItem("session_token") || ""; } catch { return ""; }
  })();
  const base = process.env.REACT_APP_BACKEND_URL || "";
  const csvUrl = (kind) =>
    `${base}/api/admin/audit/csv?kind=${kind}&min_usd=${minUsd}`;

  const download = async (kind, filenameHint) => {
    try {
      const resp = await fetch(csvUrl(kind), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) {
        toast.error(`Download failed (${resp.status})`);
        return;
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const cd = resp.headers.get("content-disposition") || "";
      const m = cd.match(/filename="?([^";]+)"?/i);
      a.download = m ? m[1] : filenameHint;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Network error — could not fetch CSV");
    }
  };

  // ── Founder Platform Health (derived from KPIs) ──────────────
  const platformHealth = kpis ? (() => {
    const revenue = kpis.total_gift_revenue_usd || 0;
    const liability = (kpis.total_creator_earnings_owed_usd || 0) + (kpis.total_pending_in_hold_usd || 0);
    const netRevenue = kpis.platform_revenue_retained_usd || 0;
    // Reserve coverage = (Revenue Retained + Already Paid Out + Liability) / Revenue
    // Simpler/founder-friendly: how much of outstanding creator liability is covered by retained revenue + reserves
    const reserveCoverage = liability > 0
      ? Math.min(100, Math.round((netRevenue / liability) * 100))
      : 100;
    return { revenue, liability, netRevenue, reserveCoverage };
  })() : null;

  // Drift classification counts (from driftDetail when available)
  const driftSummary = driftDetail?.summary || {};

  return (
    <div className="min-h-screen bg-[#0b0b13] text-white">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
          <div>
            <span className="text-xs uppercase tracking-[0.3em] text-emerald-300 font-bold">📊 Admin · Financial Control</span>
            <h1 className="text-3xl sm:text-4xl font-black mt-1 tracking-tight">Payout Audit & Economy</h1>
            <p className="text-gray-400 text-sm mt-2 max-w-2xl">
              Live/Gifts wallet reconciliation, gift-economy KPIs, and drift
              remediation. Every withdrawable dollar must be backed by a
              credited Stripe coin purchase, and the{" "}
              <code className="text-pink-300">coin_wallets</code> cache must
              match the <code className="text-pink-300">earnings_entries</code>{" "}
              source of truth. <strong>Cannot</strong> approve or mark payouts
              paid — that stays in{" "}
              <Link to="/admin/payouts" className="text-fuchsia-300 underline">
                /admin/payouts
              </Link>.
            </p>
          </div>
        </div>

        {/* ── Payout Readiness Banner (tri-state) ───────────────── */}
        {verdict === "green" && (
          <Banner tone="good" icon="✅" title="GREEN — Safe to disburse" testId="audit-verdict-green">
            No orphan revenue, no cache drift, no DO NOT PAY rows. Eligible
            creators are clean — proceed with payouts in{" "}
            <Link to="/admin/payouts" className="underline">/admin/payouts</Link>.
          </Banner>
        )}
        {verdict === "yellow" && (
          <Banner tone="warn" icon="⚠" title="YELLOW — Drift present, eligible creators unaffected" testId="audit-verdict-yellow">
            {fmtInt(integrity.creators_with_drift)} wallet(s) drift detected,
            but payouts can still proceed for ELIGIBLE creators. Use the Drift
            Management panel below to clear test-account drift safely.
          </Banner>
        )}
        {verdict === "red" && (
          <Banner tone="bad" icon="⛔" title="RED — Orphan revenue or unresolved issues" testId="audit-verdict-red">
            Orphan paid entries detected: {fmtMoney(usd.orphan_flagged_total)}.
            Coins classified {`"paid"`} have no upstream{" "}
            <code>coin_purchases</code> row for the sender. <strong>DO NOT
            DISBURSE</strong> until investigated.
          </Banner>
        )}

        {/* ── Controls ───────────────────────────────────────────── */}
        <div className="rounded-xl border border-white/10 bg-white/5 p-4 mb-6 flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-gray-400 font-bold">
              Min USD for payout-ready filter
            </label>
            <input
              type="number" min="0" step="1" value={minUsd}
              onChange={(e) => setMinUsd(Number(e.target.value) || 0)}
              className="mt-1 w-28 bg-black/40 border border-white/10 rounded px-2 py-1.5 text-sm font-mono"
              data-testid="audit-min-usd-input"
            />
          </div>
          <Button
            onClick={() => runAudit(0)}
            disabled={running}
            className="bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-400/40 text-emerald-100"
            data-testid="audit-run-full"
          >
            {running ? "Running…" : "▶ Run full platform audit"}
          </Button>
          <Button
            onClick={() => runAudit(minUsd)}
            disabled={running}
            className="bg-amber-500/20 hover:bg-amber-500/30 border border-amber-400/40 text-amber-100"
            data-testid="audit-run-payout-ready"
          >
            {running ? "Running…" : `▶ Run payout-ready audit (≥ $${minUsd})`}
          </Button>
          <Button
            onClick={loadKpis}
            disabled={loadingKpis}
            variant="outline"
            className="border-fuchsia-400/40 text-fuchsia-100 hover:bg-fuchsia-500/10"
            data-testid="audit-refresh-kpis"
          >
            {loadingKpis ? "Loading…" : "🔄 Refresh KPIs"}
          </Button>
        </div>

        {/* ── Gift Economy Dashboard (KPIs) ──────────────────────── */}
        <div className="mb-6" data-testid="audit-kpi-section">
          <SectionHeader
            kicker="💎 Live Gift Economy"
            title="Platform KPI Dashboard"
            subtitle="Live numbers from Stripe coin_purchases, gift_transactions, and creator wallets."
          />
          {kpis ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              <Card label="Total Gift Revenue" value={fmtMoney(kpis.total_gift_revenue_usd)} tone="accent" testId="kpi-gift-revenue" hint={`${fmtInt(kpis.successful_purchases)} purchases · ${fmtInt(kpis.unique_buyers)} buyers`} />
              <Card label="Creator Earnings Owed" value={fmtMoney(kpis.total_creator_earnings_owed_usd)} tone="warn" testId="kpi-earnings-owed" hint="Withdrawable, not yet paid" />
              <Card label="Creator Earnings Paid" value={fmtMoney(kpis.total_creator_earnings_paid_usd)} tone="good" testId="kpi-earnings-paid" hint="Lifetime disbursed" />
              <Card label="Pending in 7-day Hold" value={fmtMoney(kpis.total_pending_in_hold_usd)} testId="kpi-pending-hold" />
              <Card label="Pending Payout Requests" value={fmtInt(kpis.pending_payout_requests)} tone={kpis.pending_payout_requests ? "warn" : "default"} testId="kpi-pending-requests" hint="Awaiting admin review" />
              <Card label="Platform Revenue Retained" value={fmtMoney(kpis.platform_revenue_retained_usd)} tone="accent" testId="kpi-platform-retained" hint="Revenue − all creator earnings" />
              <Card label="Eligible Creators" value={fmtInt(kpis.eligible_creator_count)} tone="good" testId="kpi-eligible-creators" hint="≥ $25, no drift" />
              <Card label="Avg Creator Earnings" value={fmtMoney(kpis.average_creator_earnings_usd)} testId="kpi-avg-creator" hint={`${fmtInt(kpis.unique_earning_creators)} earners`} />
              <Card label="Total Coins Purchased" value={fmtInt(kpis.total_coins_purchased)} testId="kpi-coins-purchased" />
              <Card label="Total Coins Gifted" value={fmtInt(kpis.total_coins_gifted)} testId="kpi-coins-gifted" hint={`${fmtInt(kpis.total_gifts_sent)} gifts sent`} />
            </div>
          ) : (
            <div className="text-xs text-gray-400">
              {loadingKpis ? "Loading KPIs…" : "No KPI data yet — click Refresh KPIs above."}
            </div>
          )}
        </div>

        {/* ── Founder Visibility — Platform Health ───────────────── */}
        {platformHealth && (
          <div className="mb-6" data-testid="audit-platform-health-section">
            <SectionHeader
              kicker="🏛 Founder Visibility"
              title="Platform Health"
              subtitle="Top-line solvency view: revenue vs. creator liability."
            />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Card label="Revenue Collected" value={fmtMoney(platformHealth.revenue)} tone="accent" testId="health-revenue" />
              <Card label="Creator Liability" value={fmtMoney(platformHealth.liability)} tone="warn" testId="health-liability" hint="Owed + in hold" />
              <Card label="Net Platform Revenue" value={fmtMoney(platformHealth.netRevenue)} tone={platformHealth.netRevenue >= 0 ? "good" : "bad"} testId="health-net-revenue" />
              <Card
                label="Payout Reserve Coverage"
                value={`${platformHealth.reserveCoverage}%`}
                tone={platformHealth.reserveCoverage >= 100 ? "good" : platformHealth.reserveCoverage >= 50 ? "warn" : "bad"}
                testId="health-reserve-coverage"
                hint="Net revenue ÷ liability"
              />
            </div>
          </div>
        )}

        {/* ── Totals grid (audit summary) ────────────────────────── */}
        <div className="mb-6">
          <SectionHeader
            kicker="📋 Audit Totals"
            title="Per-Creator Reconciliation Summary"
          />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            <Card label="Eligible to pay" value={fmtMoney(usd.eligible_total)} tone="good" testId="audit-card-eligible-usd" />
            <Card label="Eligible creators" value={fmtInt(counts.eligible)} tone="good" testId="audit-card-eligible-count" />
            <Card label="Below $25 minimum" value={fmtInt(counts.below_minimum)} testId="audit-card-below-min" />
            <Card label="DO NOT PAY" value={fmtInt(counts.do_not_pay)} tone={counts.do_not_pay ? "bad" : "default"} testId="audit-card-do-not-pay" />
            <Card label="Flagged USD (not payable)" value={fmtMoney(usd.flagged_total)} tone={usd.flagged_total ? "bad" : "default"} testId="audit-card-flagged-usd" />
            <Card label="Pending in 7-day hold" value={fmtMoney(usd.pending_hold_total)} testId="audit-card-pending-usd" />
            <Card label="Lifetime paid out" value={fmtMoney(usd.lifetime_paid_out_total)} testId="audit-card-lifetime-paid" />
            <Card label="Orphan-flagged USD" value={fmtMoney(usd.orphan_flagged_total)} tone={usd.orphan_flagged_total ? "bad" : "default"} testId="audit-card-orphan-usd" />
            <Card label="Creators with drift" value={fmtInt(integrity.creators_with_drift)} tone={integrity.creators_with_drift ? "warn" : "default"} testId="audit-card-drift-count" />
            <Card
              label={`Payout-ready ≥ $${minUsd}`}
              value={fmtMoney(usd.payout_ready_total)}
              tone="good"
              testId="audit-card-payout-ready-usd"
            />
          </div>
        </div>

        {/* ── Drift Management Panel (Phase 16.3) ────────────────── */}
        {(warnings.drift_present || warnings.do_not_pay_present) && (
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 mb-6" data-testid="drift-management-panel">
            <SectionHeader
              kicker="🛠 Drift Management"
              title="Reset TEST_ACCOUNT Cache Drift"
              subtitle="Mutates ONLY wallets classified TEST_ACCOUNT by backend heuristics (test/seed email patterns AND zero paid earnings). LIKELY_REAL and UNKNOWN are always skipped."
            />

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <Card label="Total Drift Creators" value={fmtInt(integrity.creators_with_drift)} tone="warn" testId="drift-count-total" />
              <Card label="TEST_ACCOUNT" value={fmtInt(driftSummary.test_accounts)} tone="good" testId="drift-count-test" hint="Safe to reset" />
              <Card label="LIKELY_REAL" value={fmtInt(driftSummary.likely_real)} tone={driftSummary.likely_real ? "bad" : "default"} testId="drift-count-real" hint="Will be skipped" />
              <Card label="UNKNOWN" value={fmtInt(driftSummary.unknown)} tone={driftSummary.unknown ? "warn" : "default"} testId="drift-count-unknown" hint="Will be skipped" />
            </div>

            <div className="flex flex-wrap gap-2 mb-3">
              <Button
                onClick={runDryRun}
                disabled={dryRunning}
                className="bg-amber-500/20 hover:bg-amber-500/30 border border-amber-400/40 text-amber-100"
                data-testid="drift-dry-run-btn"
              >
                {dryRunning ? "Running…" : "🔍 Dry Run (preview only)"}
              </Button>
              <Button
                size="sm" variant="outline"
                onClick={loadDriftDetail}
                disabled={loadingDrift}
                className="border-rose-400/40 text-rose-100 hover:bg-rose-500/10"
                data-testid="audit-load-drift-detail"
              >
                {loadingDrift ? "Loading…" : "🔄 Refresh drift detail"}
              </Button>
            </div>

            {/* Dry-run results */}
            {dryRunResult && (
              <div className="rounded-lg border border-amber-400/30 bg-black/30 p-3 mb-3" data-testid="drift-dry-run-result">
                <div className="text-xs font-bold text-amber-200 mb-2">
                  Dry Run — would reset {fmtInt(dryRunResult.summary?.would_mutate)} TEST_ACCOUNT wallet(s) ·
                  skipping {fmtInt(dryRunResult.summary?.skipped_likely_real)} LIKELY_REAL ·
                  {fmtInt(dryRunResult.summary?.skipped_unknown)} UNKNOWN
                </div>
                {dryRunResult.dry_run_diffs?.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-[11px]" data-testid="drift-dry-run-table">
                      <thead className="text-[10px] uppercase tracking-widest text-gray-400">
                        <tr>
                          <th className="px-2 py-1 text-left">Email</th>
                          <th className="px-2 py-1 text-right">avail before → after</th>
                          <th className="px-2 py-1 text-right">paid_out before → after</th>
                          <th className="px-2 py-1 text-right">nonwith before → after</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dryRunResult.dry_run_diffs.map((d) => (
                          <tr key={d.user_id} className="border-t border-white/5">
                            <td className="px-2 py-1 font-mono text-gray-300">{d.email || "(no email)"}</td>
                            <td className="px-2 py-1 text-right font-mono">{d.before?.withdrawable_earnings_available} → {d.after?.withdrawable_earnings_available}</td>
                            <td className="px-2 py-1 text-right font-mono">{d.before?.total_paid_out_coins} → {d.after?.total_paid_out_coins}</td>
                            <td className="px-2 py-1 text-right font-mono">{d.before?.non_withdrawable_earnings} → {d.after?.non_withdrawable_earnings}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* CONFIRM-typed reset */}
                {dryRunResult.summary?.would_mutate > 0 && (
                  <div className="mt-4 pt-3 border-t border-amber-400/20">
                    <label className="block text-[10px] uppercase tracking-widest text-rose-200 font-bold mb-1">
                      Type <code className="px-1 bg-rose-500/20 rounded">CONFIRM</code> to execute the reset above
                    </label>
                    <div className="flex flex-wrap gap-2">
                      <input
                        type="text"
                        value={confirmText}
                        onChange={(e) => setConfirmText(e.target.value)}
                        placeholder="CONFIRM"
                        className="bg-black/40 border border-rose-400/30 rounded px-2 py-1.5 text-sm font-mono w-40"
                        data-testid="drift-reset-confirm-input"
                      />
                      <Button
                        onClick={runReset}
                        disabled={resetting || confirmText.trim() !== "CONFIRM"}
                        className="bg-rose-500/30 hover:bg-rose-500/40 border border-rose-400/50 text-rose-50 disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid="drift-reset-execute-btn"
                      >
                        {resetting ? "Resetting…" : "⚠ Reset TEST_ACCOUNT Drift"}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Post-reset results */}
            {resetResult && (
              <div className="rounded-lg border border-emerald-400/30 bg-emerald-500/10 p-3 mb-3" data-testid="drift-reset-result">
                <div className="text-sm font-bold text-emerald-100">
                  ✅ Reset complete — {fmtInt(resetResult.summary?.mutated)} TEST_ACCOUNT wallet(s) cleared
                </div>
                <div className="text-[11px] text-emerald-200/80 mt-1">
                  Skipped {fmtInt(resetResult.summary?.skipped_likely_real)} LIKELY_REAL ·
                  {fmtInt(resetResult.summary?.skipped_unknown)} UNKNOWN — these were NOT touched.
                </div>
              </div>
            )}

            {/* Drift detail table */}
            {driftDetail?.details?.length ? (
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="audit-drift-table">
                  <thead className="bg-black/30 text-[10px] uppercase tracking-widest text-gray-400">
                    <tr>
                      <th className="px-2 py-2 text-left">Creator</th>
                      <th className="px-2 py-2 text-left">Class</th>
                      <th className="px-2 py-2 text-right">paid entries</th>
                      <th className="px-2 py-2 text-right">cache lifetime</th>
                      <th className="px-2 py-2 text-right">cache paid_out</th>
                      <th className="px-2 py-2 text-left">Drift breakdown</th>
                      <th className="px-2 py-2 text-left">Recommendation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {driftDetail.details.map((d) => {
                      const driftBits = [];
                      if (d.drift_available) driftBits.push(`avail ${d.drift_available > 0 ? "+" : ""}${d.drift_available}`);
                      if (d.drift_pending) driftBits.push(`pending ${d.drift_pending > 0 ? "+" : ""}${d.drift_pending}`);
                      if (d.drift_nonwith) driftBits.push(`nonwith ${d.drift_nonwith > 0 ? "+" : ""}${d.drift_nonwith}`);
                      if (d.drift_paid_out) driftBits.push(`paid_out ${d.drift_paid_out > 0 ? "+" : ""}${d.drift_paid_out}`);
                      const classCls = {
                        TEST_ACCOUNT: "bg-emerald-500/20 text-emerald-200 border-emerald-400/40",
                        LIKELY_REAL:  "bg-rose-500/20 text-rose-200 border-rose-400/40",
                        UNKNOWN:      "bg-amber-500/20 text-amber-200 border-amber-400/40",
                      }[d.classification] || "bg-gray-500/20 text-gray-300 border-gray-400/40";
                      return (
                        <tr key={d.user_id} className="border-t border-white/5" data-testid={`audit-drift-row-${d.user_id}`}>
                          <td className="px-2 py-2">
                            <div className="font-semibold">{d.artist_name || "(no name)"}</div>
                            <div className="text-[10px] text-gray-500 font-mono">{d.email}</div>
                          </td>
                          <td className="px-2 py-2">
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${classCls}`} title={d.classification_reason}>
                              {d.classification}
                            </span>
                          </td>
                          <td className="px-2 py-2 text-right font-mono">{d.earnings_entries_paid}</td>
                          <td className="px-2 py-2 text-right font-mono text-gray-400">{(d.cache_snapshot?.total_earned_coins || 0).toLocaleString()}</td>
                          <td className="px-2 py-2 text-right font-mono text-gray-400">{(d.cache_snapshot?.total_paid_out_coins || 0).toLocaleString()}</td>
                          <td className="px-2 py-2 font-mono text-[11px] text-amber-200">{driftBits.join(", ") || "—"}</td>
                          <td className="px-2 py-2 text-[11px] text-gray-300">{d.remediation_recommendation}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : loadingDrift ? (
              <div className="text-xs text-gray-400">Loading drift detail…</div>
            ) : null}
          </div>
        )}

        {/* ── CSV downloads ──────────────────────────────────────── */}
        <div className="rounded-xl border border-white/10 bg-white/5 p-4 mb-6">
          <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-bold mb-2">📥 Download CSVs</div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => download("full", "rgm_audit_full.csv")}
              data-testid="audit-download-full"
              className="border-emerald-400/40 text-emerald-100 hover:bg-emerald-500/10"
            >
              ⬇ Full audit CSV
            </Button>
            <Button
              variant="outline"
              onClick={() => download("summary", "rgm_audit_summary.csv")}
              data-testid="audit-download-summary"
              className="border-emerald-400/40 text-emerald-100 hover:bg-emerald-500/10"
            >
              ⬇ Summary CSV
            </Button>
            <Button
              variant="outline"
              onClick={() => download("payout-ready", "rgm_audit_payout_ready.csv")}
              data-testid="audit-download-payout-ready"
              className="border-amber-400/40 text-amber-100 hover:bg-amber-500/10"
            >
              ⬇ Payout-ready CSV (≥ ${minUsd})
            </Button>
          </div>
          <p className="text-xs text-gray-400 mt-3">
            All CSVs are produced live, read-only, and never cached.
          </p>
        </div>

        {/* ── Preview rows (top 100) ─────────────────────────────── */}
        {rows.length > 0 && (
          <div className="rounded-xl border border-white/10 bg-white/5 overflow-x-auto">
            <table className="w-full text-sm" data-testid="audit-preview-table">
              <thead className="bg-black/30 text-[10px] uppercase tracking-widest text-gray-400">
                <tr>
                  <th className="px-3 py-2 text-left">#</th>
                  <th className="px-3 py-2 text-left">Creator</th>
                  <th className="px-3 py-2 text-right">Withdrawable</th>
                  <th className="px-3 py-2 text-right">Pending</th>
                  <th className="px-3 py-2 text-right">Lifetime paid</th>
                  <th className="px-3 py-2 text-left">Eligibility</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr
                    key={r.user_id}
                    className="border-t border-white/5"
                    data-testid={`audit-row-${r.user_id}`}
                  >
                    <td className="px-3 py-2 text-gray-500">{i + 1}</td>
                    <td className="px-3 py-2">
                      <div className="font-semibold">{r.artist_name || "(no name)"}</div>
                      <div className="text-xs text-gray-500">{r.email}</div>
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{fmtMoney(r.withdrawable_usd)}</td>
                    <td className="px-3 py-2 text-right font-mono text-gray-400">{fmtMoney(r.pending_hold_usd)}</td>
                    <td className="px-3 py-2 text-right font-mono text-gray-500">{fmtMoney(r.already_paid_out_usd)}</td>
                    <td className="px-3 py-2">
                      {r.payout_eligibility === "ELIGIBLE" ? (
                        <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-200 border border-emerald-400/40">
                          ELIGIBLE
                        </span>
                      ) : r.payout_eligibility.startsWith("DO NOT PAY") ? (
                        <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-rose-500/20 text-rose-200 border border-rose-400/40" title={r.eligibility_reason}>
                          DO NOT PAY
                        </span>
                      ) : (
                        <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-gray-500/20 text-gray-300 border border-gray-400/40">
                          BELOW MIN
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {report?.total_rows > rows.length && (
              <div className="px-3 py-2 text-xs text-gray-400 border-t border-white/5">
                Showing first {rows.length} of {report.total_rows} creators — download the full CSV above for the complete list.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
